/**
 * Reusable authenticated fetch utility.
 *
 * Extracts the token-refresh + 401-retry + logout-dispatch pattern that was
 * previously inlined in PdfRenderer.  Used by both PdfRenderer and the PDF
 * cache service so we don't duplicate auth logic.
 */

import { tokenStorage } from './tokenStorage';
import { attemptSilentRefresh } from '../services/httpClient';

export class AuthFetchError extends Error {
  status: number;
  constructor(status: number) {
    super(`Authenticated fetch failed: ${status}`);
    this.status = status;
  }
}

/**
 * Fetch a URL with the stored Bearer token.
 *
 * 1. Checks token expiry and silently refreshes if needed.
 * 2. Retries once on 401 after a silent refresh.
 * 3. Dispatches `auth:logout` if the session is truly expired.
 */
export async function authenticatedFetch(url: string): Promise<Response> {
  let token = tokenStorage.getToken();

  // Pre-emptive refresh if the token looks expired
  if (!token || tokenStorage.isTokenExpired(token)) {
    const refreshed = await attemptSilentRefresh();
    if (refreshed) token = tokenStorage.getToken();
  }

  let response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });

  // Retry once after silent refresh on 401
  if (response.status === 401) {
    const refreshed = await attemptSilentRefresh();
    if (refreshed) {
      token = tokenStorage.getToken();
      response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
    }
    if (response.status === 401) {
      tokenStorage.clearAll();
      window.dispatchEvent(new CustomEvent('auth:logout'));
      throw new AuthFetchError(401);
    }
  }

  if (!response.ok) throw new AuthFetchError(response.status);

  return response;
}
